import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search, User, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../ui/sheet';

const CATEGORIES = [
  { name: 'Skincare', slug: 'skincare' },
  { name: 'Makeup', slug: 'makeup' },
  { name: 'Haircare', slug: 'haircare' },
  { name: 'Fragrance', slug: 'fragrance' },
  { name: 'Bath & Body', slug: 'bath-body' },
  { name: 'Tools', slug: 'tools' },
];

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
      }`}
    >
      {/* Top bar */}
      <div className="bg-[#1A1A1A] text-white text-xs py-2 text-center tracking-widest">
        FREE SHIPPING ON ORDERS OVER $50 | SHOP NOW
      </div>

      <nav className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <button data-testid="mobile-menu-btn" className="p-2">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] p-0">
              <div className="flex flex-col h-full">
                <div className="p-6 border-b">
                  <Link to="/" className="font-heading text-2xl font-semibold" onClick={() => setMobileMenuOpen(false)}>
                    BLUSH & GO
                  </Link>
                </div>
                <div className="flex-1 overflow-auto py-6">
                  <div className="px-6 space-y-1">
                    {CATEGORIES.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="block py-3 text-sm uppercase tracking-widest hover:text-neutral-500 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={`mobile-nav-${cat.slug}`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="p-6 border-t space-y-3">
                  {user ? (
                    <>
                      <p className="text-sm text-neutral-500">Hello, {user.name}</p>
                      {user.is_admin && (
                        <Link
                          to="/admin"
                          className="block text-sm uppercase tracking-widest"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Admin Dashboard
                        </Link>
                      )}
                      <button
                        onClick={() => { logout(); setMobileMenuOpen(false); }}
                        className="text-sm uppercase tracking-widest text-red-600"
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      className="block text-sm uppercase tracking-widest"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link to="/" className="font-heading text-xl md:text-2xl font-semibold tracking-tight" data-testid="logo">
            BLUSH & GO
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`nav-link text-xs uppercase tracking-widest font-medium transition-colors ${
                  isActive(`/category/${cat.slug}`) ? 'text-neutral-900' : 'text-neutral-600 hover:text-neutral-900'
                }`}
                data-testid={`nav-${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            <button className="hidden md:block p-2 hover:bg-neutral-100 transition-colors" data-testid="search-btn">
              <Search className="w-5 h-5" />
            </button>
            
            {user ? (
              <div className="flex items-center gap-3">
                <Link to={user.is_admin ? "/admin" : "/login"} className="p-2 hover:bg-neutral-100 transition-colors" data-testid="user-menu-btn">
                  <User className="w-5 h-5" />
                </Link>
                <button 
                  onClick={logout} 
                  className="hidden md:block text-xs uppercase tracking-widest hover:text-neutral-500"
                  data-testid="logout-btn"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="text-xs uppercase tracking-widest hover:text-neutral-500 transition-colors"
                data-testid="login-link"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navigation;
